import { Box } from "@avenue-8/sidekick-ui";
import { Checkbox, IconButton } from "@mui/material";
import {
  PropertyPhoto,
  SideBySideComparisonItem,
  SideBySideComparisonSectionView,
  SideBySideComparisonSectionConfig,
} from "@cma/presentation-generation-logic";
import { ComparisonPropertyFieldItem } from "./ComparisonPropertyFieldItem";
import { useImageGalleryStore } from "@cma/store/image-gallery.store";
import styled from "@emotion/styled";
import { useTheme } from "@emotion/react";
import { ResponsiveImage } from "../../../shared/ResponsiveImage";
import { Typography } from "@mui/material";
import { textFormatter } from "@shared/utils/text-formatter";
import { toast } from "react-toastify";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { DraggableAttributes } from "@dnd-kit/core";
import { useSortable } from "@dnd-kit/sortable";

type DragListeners = ReturnType<typeof useSortable>["listeners"];
type DisplayPreferences = SideBySideComparisonSectionView["preferences"];

interface ComparisonPropertyItemProps {
  highlight?: boolean;
  fixedLabels?: boolean;
  // displayMode: "mobile" | "desktop" | "print" | "tablet";
  propertyData?: SideBySideComparisonItem;
  gallery: PropertyPhoto[];
  preferences?: DisplayPreferences;
  isSubjectProperty?: boolean;
  hideLabels?: boolean;
  onRemove?: (propertyId: string) => void;
  onHideField?: (fieldKey: keyof SideBySideComparisonSectionConfig) => void;
  isEditingNotes?: boolean;
  onStartEditNotes?: (propertyId: string) => void;
  onSaveNotes?: (notes: string) => void;
  onCancelEditNotes?: () => void;
  editMode?: boolean;
  dragHandleRef?: React.Ref<HTMLButtonElement>;
  dragHandleListeners?: DragListeners;
  dragHandleAttributes?: DraggableAttributes;
}

const Container = styled(Box)`
  padding: 1.5rem 1rem 1rem;
  width: 100%;
  margin: auto;
  min-width: 0;
  position: relative;

  &:hover .drag-handle-bar {
    opacity: 1;
  }

  ${(p) => p.theme.media.mobileDown} {
    max-width: 160px;
  }
`;

const DragHandleBar = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 1.5rem;
  opacity: 0;
  transition: opacity 0.15s ease-in-out;
`;

const DragHandle = styled(IconButton)`
  cursor: grab;
  touch-action: none;

  &:active {
    cursor: grabbing;
  }
` as typeof IconButton;

const CoverPhotoContainer = styled(Box)`
  position: relative;
  width: 100%;
  aspect-ratio: 16/9;
  border-radius: 0.5rem;
  overflow: hidden;
  margin-bottom: 1rem;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${(p) => p.theme.colors.background2};
  overflow: hidden;

  p {
    font-size: ${(p) => p.theme.text.fontSizes.xs};
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 0.5rem;
    transition: all 200ms ease-in-out;

    &:hover {
      transform: scale(1.1);
    }
  }
`;

const ContentContainer = styled(Box)`
  display: flex;
  width: 100%;
  text-align: left;
  flex-direction: column;
  gap: 0.75rem;
`;

const AddressLine = styled(Typography)`
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  min-height: 2.75rem;
`;

const CheckboxContainer = styled.div`
  position: absolute;
  z-index: 2;
  display: flex;
  justify-content: center;
  align-items: center;
  top: 0.25rem;
  right: 0.25rem;
  background-color: ${(p) => p.theme.colors.background};
  width: 1.375rem;
  height: 1.375rem;
  border-radius: 0.25rem;
`;

export const ComparisonPropertyItem = (props: ComparisonPropertyItemProps) => {
  const {
    // highlight = false,
    gallery,
    isSubjectProperty,
    // fixedLabels,
    hideLabels,
    propertyData,
    onRemove,
    preferences,
    onHideField,
    isEditingNotes,
    onStartEditNotes,
    onSaveNotes,
    onCancelEditNotes,
    editMode,
    dragHandleRef,
    dragHandleListeners,
    dragHandleAttributes,
  } = props;
  const setGalleryImages = useImageGalleryStore(
    (store) => store.setGalleryImages,
  );
  const galleryPhotos = (gallery ?? []).map((photo, i) => ({
    id: `id-${i}`,
    url: photo.url,
    alt: photo.caption || `Property photo ${i}`,
  }));

  const theme = useTheme();

  const handleRemoveProperty = () => {
    if (propertyData?.id && onRemove && !isSubjectProperty) {
      setTimeout(() => {
        onRemove(propertyData.id);
        toast.success(
          <Typography>
            Property <strong>{propertyData.addressLine1}</strong> removed from
            comparable properties.
          </Typography>,
        );
      }, 250);
    }
  };

  const shouldShowField = (
    fieldKey: keyof SideBySideComparisonSectionConfig,
  ) => {
    if (preferences) {
      const value = preferences[fieldKey as keyof DisplayPreferences];
      return value !== false;
    }

    const fieldDataMap = {
      showPropertyType: propertyData?.propertyType ?? propertyData?.type,
      showListingStatus: propertyData?.listingStatus,
      showListPrice: propertyData?.currentPrice,
      showListDate: propertyData?.listDate,
      showSoldPrice: propertyData?.soldPrice,
      showSoldDate: propertyData?.soldDate,
      showYearBuilt: propertyData?.yearBuilt,
      showNeighborhood: propertyData?.neighborhood,
      showLivingSpace: propertyData?.livingSpace,
      showBeds: propertyData?.beds,
      showBaths: propertyData?.baths,
      showLotSize: propertyData?.lotSize,
      showHOAFees: propertyData?.hoaFees ?? propertyData?.hoaFee,
      showGarageParkingSpaces: propertyData?.garageParkingSpaces,
      showPricePerLivingSpaceSqft: propertyData?.costPerInternalSqFt,
      showRoomsTotal: propertyData?.roomsTotal,
      showDoormanAttendance: propertyData?.hasDoormanAttendance,
      showRecurringCosts: propertyData?.totalMonthlies,
      showFlipTaxRemarks: propertyData?.flipTaxRemarks,
      showDaysOnMarket: propertyData?.daysOnMarket,
      showCumulativeDaysOnMarket: propertyData?.cdom,
      showNotes: propertyData?.notes,
    };

    const fieldData = fieldDataMap[fieldKey as keyof typeof fieldDataMap];
    return fieldData !== null && fieldData !== undefined;
  };

  const coverPhoto = galleryPhotos[0]?.url || "";

  return (
    <Container
      data-testid="comparison-property-item"
      sx={{
        background: isSubjectProperty
          ? theme.colors.background2
          : theme.colors.background,
        borderRadius: isSubjectProperty ? "0.5rem" : "none",
      }}
    >
      {dragHandleListeners && (
        <DragHandleBar className="drag-handle-bar">
          <DragHandle
            ref={dragHandleRef}
            size="small"
            {...dragHandleListeners}
            {...dragHandleAttributes}
            onPointerDown={(e) => {
              e.stopPropagation();
              dragHandleListeners.onPointerDown?.(e);
            }}
          >
            <DragIndicatorIcon
              fontSize="small"
              sx={{ transform: "rotate(90deg)" }}
            />
          </DragHandle>
        </DragHandleBar>
      )}

      <CoverPhotoContainer
        onClick={() => setGalleryImages({ images: galleryPhotos })}
        sx={{ cursor: "pointer" }}
      >
        {!isSubjectProperty && onRemove && (
          <CheckboxContainer onClick={(e) => e.stopPropagation()}>
            <Checkbox
              defaultChecked={true}
              size="small"
              onChange={handleRemoveProperty}
            />
          </CheckboxContainer>
        )}
        <ResponsiveImage
          src={coverPhoto}
          key={coverPhoto}
          alt={galleryPhotos[0]?.alt || "Cover Photo"}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            borderRadius: "0.5rem",
          }}
        />
      </CoverPhotoContainer>
      <ContentContainer>
        <AddressLine>
          {textFormatter.formatFullAddress({
            addressLine1: propertyData?.addressLine1 || "",
            addressLine2: propertyData?.addressLine2 || "",
          })}
        </AddressLine>
        {shouldShowField("showPropertyType") && (
          <ComparisonPropertyFieldItem
            label="Property Type"
            value={textFormatter.valueToText(
              propertyData?.propertyType ?? propertyData?.type,
            )}
            fieldKey="showPropertyType"
            onHide={isSubjectProperty ? onHideField : undefined}
            editMode={editMode && isSubjectProperty}
            hideLabel={hideLabels}
          />
        )}
        {shouldShowField("showListingStatus") && (
          <ComparisonPropertyFieldItem
            label="Status"
            value={textFormatter.valueToText(propertyData?.listingStatus)}
            fieldKey="showListingStatus"
            onHide={isSubjectProperty ? onHideField : undefined}
            editMode={editMode && isSubjectProperty}
            hideLabel={hideLabels}
          />
        )}
        {shouldShowField("showListPrice") && (
          <ComparisonPropertyFieldItem
            label="List Price"
            value={
              propertyData?.currentPrice
                ? textFormatter.currency(propertyData.currentPrice)
                : "-"
            }
            fieldKey="showListPrice"
            onHide={isSubjectProperty ? onHideField : undefined}
            editMode={editMode && isSubjectProperty}
            hideLabel={hideLabels}
          />
        )}
        {shouldShowField("showListDate") && (
          <ComparisonPropertyFieldItem
            label="List Date"
            value={
              propertyData?.listDate
                ? textFormatter.date(propertyData.listDate)
                : "-"
            }
            fieldKey="showListDate"
            onHide={isSubjectProperty ? onHideField : undefined}
            editMode={editMode && isSubjectProperty}
            hideLabel={hideLabels}
          />
        )}
        {shouldShowField("showSoldPrice") && (
          <ComparisonPropertyFieldItem
            label="Sold Price"
            value={
              propertyData?.soldPrice
                ? textFormatter.currency(propertyData.soldPrice)
                : "-"
            }
            fieldKey="showSoldPrice"
            onHide={isSubjectProperty ? onHideField : undefined}
            editMode={editMode && isSubjectProperty}
            hideLabel={hideLabels}
          />
        )}
        {shouldShowField("showSoldDate") && (
          <ComparisonPropertyFieldItem
            label="Sold Date"
            value={
              propertyData?.soldDate
                ? textFormatter.date(propertyData.soldDate)
                : "-"
            }
            fieldKey="showSoldDate"
            onHide={isSubjectProperty ? onHideField : undefined}
            editMode={editMode && isSubjectProperty}
            hideLabel={hideLabels}
          />
        )}
        {shouldShowField("showYearBuilt") && (
          <ComparisonPropertyFieldItem
            label="Year Built"
            value={textFormatter.valueToText(propertyData?.yearBuilt)}
            fieldKey="showYearBuilt"
            onHide={isSubjectProperty ? onHideField : undefined}
            editMode={editMode && isSubjectProperty}
            hideLabel={hideLabels}
          />
        )}
        {shouldShowField("showNeighborhood") && (
          <ComparisonPropertyFieldItem
            label="Neighborhood"
            value={textFormatter.valueToText(propertyData?.neighborhood)}
            fieldKey="showNeighborhood"
            onHide={isSubjectProperty ? onHideField : undefined}
            editMode={editMode && isSubjectProperty}
            hideLabel={hideLabels}
          />
        )}
        {shouldShowField("showLivingSpace") && (
          <ComparisonPropertyFieldItem
            label="Living Area"
            value={
              propertyData?.livingSpace
                ? textFormatter.area(propertyData.livingSpace)
                : "-"
            }
            fieldKey="showLivingSpace"
            onHide={isSubjectProperty ? onHideField : undefined}
            editMode={editMode && isSubjectProperty}
            hideLabel={hideLabels}
          />
        )}
        {shouldShowField("showBeds") && (
          <ComparisonPropertyFieldItem
            label="Beds"
            value={textFormatter.valueToText(propertyData?.beds)}
            fieldKey="showBeds"
            onHide={isSubjectProperty ? onHideField : undefined}
            editMode={editMode && isSubjectProperty}
            hideLabel={hideLabels}
          />
        )}
        {shouldShowField("showBaths") && (
          <ComparisonPropertyFieldItem
            label="Baths"
            value={textFormatter.valueToText(propertyData?.baths)}
            fieldKey="showBaths"
            onHide={isSubjectProperty ? onHideField : undefined}
            editMode={editMode && isSubjectProperty}
            hideLabel={hideLabels}
          />
        )}
        {shouldShowField("showLotSize") && (
          <ComparisonPropertyFieldItem
            label="Lot Size"
            value={
              propertyData?.lotSize
                ? textFormatter.area(propertyData.lotSize)
                : "-"
            }
            fieldKey="showLotSize"
            onHide={isSubjectProperty ? onHideField : undefined}
            editMode={editMode && isSubjectProperty}
            hideLabel={hideLabels}
          />
        )}
        {shouldShowField("showHOAFees") && (
          <ComparisonPropertyFieldItem
            label="HOA Fees"
            value={
              propertyData?.hoaFees || propertyData?.hoaFee
                ? textFormatter.hoaFeeAndFrequency({
                    legacyHoaFees: propertyData?.hoaFees,
                    hoaFeeAndFrequency: propertyData?.hoaFee,
                  })
                : "-"
            }
            fieldKey="showHOAFees"
            onHide={isSubjectProperty ? onHideField : undefined}
            editMode={editMode && isSubjectProperty}
            hideLabel={hideLabels}
          />
        )}
        {shouldShowField("showGarageParkingSpaces") && (
          <ComparisonPropertyFieldItem
            label="Parking"
            value={textFormatter.valueToText(propertyData?.garageParkingSpaces)}
            fieldKey="showGarageParkingSpaces"
            onHide={isSubjectProperty ? onHideField : undefined}
            editMode={editMode && isSubjectProperty}
            hideLabel={hideLabels}
          />
        )}
        {shouldShowField("showPricePerLivingSpaceSqft") && (
          <ComparisonPropertyFieldItem
            label="Price per Sqft"
            value={
              propertyData?.costPerInternalSqFt
                ? textFormatter.currency(propertyData.costPerInternalSqFt)
                : "-"
            }
            fieldKey="showPricePerLivingSpaceSqft"
            onHide={isSubjectProperty ? onHideField : undefined}
            editMode={editMode && isSubjectProperty}
            hideLabel={hideLabels}
          />
        )}
        {shouldShowField("showRoomsTotal") && (
          <ComparisonPropertyFieldItem
            label="Total Rooms"
            value={textFormatter.valueToText(propertyData?.roomsTotal)}
            fieldKey="showRoomsTotal"
            onHide={isSubjectProperty ? onHideField : undefined}
            editMode={editMode && isSubjectProperty}
            hideLabel={hideLabels}
          />
        )}
        {shouldShowField("showDoormanAttendance") && (
          <ComparisonPropertyFieldItem
            label="Doorman / Attendance"
            value={textFormatter.valueToText(
              propertyData?.hasDoormanAttendance ? "Yes" : "No",
            )}
            fieldKey="showDoormanAttendance"
            onHide={isSubjectProperty ? onHideField : undefined}
            editMode={editMode && isSubjectProperty}
            hideLabel={hideLabels}
          />
        )}
        {shouldShowField("showRecurringCosts") && (
          <ComparisonPropertyFieldItem
            label="Recurring Costs"
            value={
              propertyData?.totalMonthlies
                ? textFormatter.currency(propertyData.totalMonthlies)
                : "-"
            }
            fieldKey="showRecurringCosts"
            onHide={isSubjectProperty ? onHideField : undefined}
            editMode={editMode && isSubjectProperty}
            hideLabel={hideLabels}
          />
        )}
        {shouldShowField("showFlipTaxRemarks") && (
          <ComparisonPropertyFieldItem
            label="Flip Tax"
            value={textFormatter.valueToText(propertyData?.flipTaxRemarks)}
            fieldKey="showFlipTaxRemarks"
            onHide={isSubjectProperty ? onHideField : undefined}
            editMode={editMode && isSubjectProperty}
            hideLabel={hideLabels}
          />
        )}
        {shouldShowField("showDaysOnMarket") && (
          <ComparisonPropertyFieldItem
            label="Days on Market"
            value={textFormatter.valueToText(propertyData?.daysOnMarket)}
            fieldKey="showDaysOnMarket"
            onHide={isSubjectProperty ? onHideField : undefined}
            editMode={editMode && isSubjectProperty}
            hideLabel={hideLabels}
          />
        )}
        {shouldShowField("showCumulativeDaysOnMarket") && (
          <ComparisonPropertyFieldItem
            label="Cumulative Days on Market"
            value={textFormatter.valueToText(propertyData?.cdom)}
            fieldKey="showCumulativeDaysOnMarket"
            onHide={isSubjectProperty ? onHideField : undefined}
            editMode={editMode && isSubjectProperty}
            hideLabel={hideLabels}
          />
        )}
        {!hideLabels && shouldShowField("showNotes") && (
          <ComparisonPropertyFieldItem
            label="Notes"
            value={
              !propertyData?.notes && onStartEditNotes
                ? "Click to edit"
                : textFormatter.valueToText(propertyData?.notes)
            }
            fieldKey="showNotes"
            onHide={onHideField}
            editMode={editMode}
            hideLabel={hideLabels}
            clampLines={8}
            isPlaceholder={!propertyData?.notes && !!onStartEditNotes}
            onEdit={
              onStartEditNotes && propertyData?.id
                ? () => onStartEditNotes(propertyData.id)
                : undefined
            }
            isEditingValue={isEditingNotes}
            editValue={propertyData?.notes ?? ""}
            onSaveValue={onSaveNotes}
            onCancelEdit={onCancelEditNotes}
          />
        )}
      </ContentContainer>
    </Container>
  );
};
